<template>
  <section>
    <LoadingIndicator v-if="submitting" />
    <ValidationObserver slim v-slot="{ invalid }">
      <b-form @submit.prevent="submit" novalidate>
        <h2>{{ $t('login.update-password-label') }}</h2>
        <p v-if="!token">{{ $t('login.update-password-description') }}</p>

        <NewPasswordControl v-model="newPassword" v-focus />

        <EwnFormControl vid="confirm-password" label-for="confirm-password" :label="$t('login.confirm-password-label')"
                        v-slot="{ state }" :rules="{ required: true, confirmed: 'new-password' }">
          <b-form-input id="confirm-password" type="password" name="confirm-password" :state="state"
                        v-model="confirmPassword" autocapitalize="off" autocomplete="new-password"
                        autocorrect="off" spellcheck="false" />
        </EwnFormControl>

        <div class="invalid-feedback" v-if="errorMessage">{{ errorMessage }}</div>

        <div class="main-button-container">
          <button class="primary" :disabled="invalid || submitting">
            {{ $t('login.update-password-button') }}
          </button>
          <div class="link-container">
            <router-link :to="{ name: 'Login', query: { 'redirect_uri': redirectUri, 'brand_template': brandTemplate } }"
                         class="button-link">
              {{ $t('login.return-button') }}
            </router-link>
            <router-link :to="{ path: '/contact-us', query: { 'brand_template': brandTemplate }}"
                         class="button-link">
              {{ $t('login.contact-button') }}
            </router-link>
          </div>
        </div>
      </b-form>
    </ValidationObserver>
  </section>
</template>

<script>
  import { defineComponent, onMounted, ref } from 'vue';
  import { BForm, BFormInput } from 'bootstrap-vue';
  import { ValidationObserver } from 'vee-validate';
  import { useForgotPassword } from './useForgotPassword';
  import { useLogin } from './useLogin';
  import EwnFormControl from '@/modules/forms/components/EwnFormControl';
  import NewPasswordControl from '@/modules/forms/components/NewPasswordControl';
  import focus from '@/modules/forms/directives/Focus';
  import LoadingIndicator from '@/modules/loading/components/LoadingIndicator';

  export default defineComponent({
    name: 'UpdatePassword',
    components: {
      'b-form': BForm,
      'b-form-input': BFormInput,
      EwnFormControl,
      LoadingIndicator,
      NewPasswordControl,
      ValidationObserver
    },
    directives: {
      focus
    },
    props: {
      token: {
        type: String
      },
      redirectUri: {
        type: String
      },
      brandTemplate: {
        type: String
      }
    },
    setup (props) {
      const submitting = ref(false);
      const newPassword = ref(null);
      const confirmPassword = ref(null);
      const errorMessage = ref(null);

      const {
        verifyResetToken,
        resetPassword
      } = useForgotPassword();

      const { updatePassword } = useLogin({
        redirectUri: props.redirectUri
      });

      if (props.token) {
        onMounted(async () => {
          submitting.value = true;
          await verifyResetToken({ token: props.token });
          submitting.value = false;
        });
      }

      return {
        confirmPassword,
        errorMessage,
        newPassword,
        async submit () {
          errorMessage.value = null;
          submitting.value = true;
          if (props.token) {
            errorMessage.value = await resetPassword({
              token: props.token,
              password: newPassword.value
            });
          } else {
            errorMessage.value = await updatePassword({
              password: newPassword.value
            });
          }
          if (!errorMessage.value) return;
          submitting.value = false;
        },
        submitting
      };
    }
  });
</script>

<style lang="scss" scoped>
  section {
    max-width: 400px;
  }

  form {
    width: 100%;
  }

  h2 {
    text-align: center;
    margin-bottom: 24px;
  }

  p {
    margin-bottom: 16px;
  }
</style>
